/**
 * Element action vocabulary — THE single source for "what can I do with this one element?".
 *
 * The same actions are reachable from two surfaces: the ElementInfoPanel — which a
 * double-click on an element opens in BOTH 3D viewers — and the Browser pane's per-row ⋯ menu.
 * They must read as ONE product, so the labels, the tooltips and — more importantly — the
 * branch (does this element already have an asset?) live here and nowhere else.
 *
 * Rendering deliberately stays with each surface: the Browser pane renders its menu INLINE
 * under the row (the pane is ~320px wide), the panel lays them out in its Actions row.
 * Sharing the markup would fight both; sharing the vocabulary is what keeps them consistent.
 */

export type ElementActionKind =
  | 'focus' | 'copyId' | 'createAsset' | 'createAssetAndAddIot' | 'createAssetAndTicket'
  | 'createTicket' | 'viewTickets' | 'addIot' | 'showInDiagram'
  | 'editAsset' | 'deleteAsset';

/**
 * Where "go to this element" lands. The action is the same intent on every surface, but the
 * wording must tell the truth about what will happen: a 3D viewer focuses the element, the
 * panorama can only open the nearest scan station, and the 2D map centres on its position.
 * Naming them here (rather than forking the pane per viewer) is what keeps one vocabulary.
 */
export type ElementFocusTarget = 'model3d' | 'panorama' | 'map2d';

export interface ElementFocusVocabulary {
  /** Menu label. */
  label: string;
  /** Menu tooltip; `subject` is the quoted element name, or "this element" when unnamed. */
  title: (subject: string) => string;
  /** Tooltip for a whole row whose click performs the same action. */
  rowTitle: (subject: string) => string;
}

export const ELEMENT_FOCUS_ACTIONS: Record<ElementFocusTarget, ElementFocusVocabulary> = {
  model3d: {
    label: '🎯 Show in 3D',
    title: (subject) => `Focus ${subject} in the 3D viewer`,
    rowTitle: (subject) => `Click to focus on ${subject}`,
  },
  panorama: {
    label: '🧭 Jump to nearest station',
    title: (subject) => `Open the panorama station nearest to ${subject}`,
    rowTitle: (subject) => `Click to jump to the panorama station nearest to ${subject}`,
  },
  map2d: {
    label: '📍 Locate on map',
    title: (subject) => `Centre the 2D map on ${subject}`,
    rowTitle: (subject) => `Click to locate ${subject} on the 2D map`,
  },
};

/**
 * Visible labels. `focus` keeps the Browser pane's existing "Show in 3D" wording — it is the
 * product's established name for that action on a 3D surface, and the viewer menu adopts it
 * rather than introducing a second word for the same thing. Surfaces that can only reach the
 * element some other way pass a `focusTarget` and get that variant's wording instead.
 */
export const ELEMENT_ACTION_LABELS: Record<ElementActionKind, string> = {
  focus: ELEMENT_FOCUS_ACTIONS.model3d.label,
  copyId: '🆔 Copy id',
  createAsset: '➕ Create asset',
  // THE LABEL SAYS AN ASSET IS MADE (product decision, 2026-09-22). An earlier version read
  // just "Add IoT" on the grounds that the asset was plumbing — but it is a real, lasting row
  // in the asset register, and a person who did not know it would be created could not know
  // to expect it. Both "make an asset so something can hang off it" actions now say so. It
  // still never appears alongside `addIot` (elementActions.test.ts pins that).
  createAssetAndAddIot: '📡 Add IoT (adds asset)',
  createAssetAndTicket: '🎫 Create ticket (adds asset)',
  createTicket: '🎫 Create ticket',
  viewTickets: '🎫 Tickets for this asset',
  addIot: '📡 Add IoT device',
  showInDiagram: '🔗 Show in system diagram',
  editAsset: '✏️ Edit asset',
  // Last in the list and the only destructive item, so it is never the neighbour of something
  // routine. Says "Delete asset", not "Delete": what goes is the asset record, while the model
  // element it was made from is untouched — the distinction people get wrong.
  deleteAsset: '🗑️ Delete asset',
};

export interface ElementActionHandlers {
  /** Focus/zoom the element in the 3D viewer. Offered for every element. */
  onFocus?: () => void;
  /** Copy the element id (RVT dbId / IFC expressId) or, on an asset row, the asset id. */
  onCopyId?: () => void;
  /** Offered ONLY when the element has no asset yet. */
  onCreateAsset?: () => void;
  /**
   * Offered ONLY when the element has no asset yet: create the asset AND bind a device in
   * one action. An IoT device binds to an ASSET (iot_devices.poi_id + the iot_device_pois
   * junction), never to a raw model element — so the asset has to exist first, and making
   * that a separate chore the user must remember is how orphan devices get created.
   */
  onCreateAssetAndAddIot?: () => void;
  /**
   * Offered ONLY when the element has no asset yet: create the asset, then open the ticket
   * dialog with it as the ticket's origin. A ticket does not strictly NEED an asset — it can
   * carry the element's dbId — but with one it shows up under "Tickets for this asset",
   * jumps to the element in 3D and appears in the system diagram. Cancelling the dialog
   * removes the asset again (shared/rollbackCreatedAsset), exactly as the IoT flow does.
   */
  onCreateAssetAndTicket?: () => void;
  /** Offered ONLY when the element has an asset — a ticket is filed against that asset. */
  onCreateTicket?: () => void;
  /**
   * Offered ONLY when the element has an asset: the tickets that already reference it
   * (service_call_pois — GET /assets/:id/service-calls?poiId=). It sits right after
   * "Create ticket" because the honest first question about a broken asset is whether
   * someone has already reported it.
   */
  onViewTickets?: () => void;
  /** Offered ONLY when the element has an asset — attach/create a device bound to it. */
  onAddIot?: () => void;
  /** Offered ONLY when the element has an asset — diagram nodes bind assets by POI id. */
  onShowInDiagram?: () => void;
  /** Offered ONLY when the element has an asset — open it for editing. */
  onEditAsset?: () => void;
  /**
   * Offered ONLY when the element has an asset. The surface is responsible for CONFIRMING
   * first, and for saying what else goes: deleting an asset cascades to its responsibility
   * grants (scope_grants), its ticket links (service_call_pois), its IoT device bindings
   * (iot_device_pois) and its room/system assignments. None of that is visible from a bare
   * "are you sure?", and none of it comes back.
   */
  onDeleteAsset?: () => void;
}

export interface ElementAction {
  kind: ElementActionKind;
  label: string;
  title: string;
  onClick: () => void;
}

/**
 * Ordered action list for one element.
 *
 * `hasAsset` picks the branch:
 *   - no asset  → Show in 3D · Copy id · Create asset · Create ticket (adds asset) · Add IoT (adds asset)
 *   - has asset → Show in 3D · Copy id · Create ticket · Tickets for this asset ·
 *                 Add IoT device · Show in system diagram · Edit asset · Delete asset
 *
 * A handler the calling surface can't honor is simply left out (the same self-gating the
 * Browser pane already uses for its viewer-coupled buttons), so a surface never renders an
 * action it cannot perform.
 */
export function buildElementActions(
  hasAsset: boolean,
  handlers: ElementActionHandlers,
  context: { name?: string; focusTarget?: ElementFocusTarget; canWrite?: boolean } = {}
): ElementAction[] {
  const subject = context.name ? `"${context.name}"` : 'this element';
  const focus = ELEMENT_FOCUS_ACTIONS[context.focusTarget || 'model3d'];
  const actions: ElementAction[] = [];

  /**
   * WRITE-ONLY ACTIONS ARE DROPPED FOR A READER, here rather than at each surface.
   *
   * Creating an asset, deleting one, attaching an IoT device and editing all need write on
   * the site (assetPois.js, iotDevices.js). The panel had no role check of any kind, so
   * every member saw all of them and got a 403 from each — and it renders on four surfaces
   * (3D, the 2D sheet, the panorama marker layer and the Browser pane), so gating it at the
   * surfaces would have meant four chances to forget. `canWrite` defaults to true so this
   * stays a subtraction for callers that have not been told the answer yet, and both viewer
   * surfaces pass it.
   *
   * Looking, copying an id, raising a ticket and jumping to the diagram are member-level on
   * the server, so they stay for everyone.
   */
  const WRITE_ACTIONS = new Set<ElementActionKind>([
    'createAsset', 'createAssetAndAddIot', 'createAssetAndTicket', 'addIot', 'editAsset', 'deleteAsset',
  ]);
  const canWrite = context.canWrite !== false;

  const push = (kind: ElementActionKind, title: string, onClick?: () => void, label?: string) => {
    if (!onClick) return;
    if (!canWrite && WRITE_ACTIONS.has(kind)) return;
    actions.push({ kind, label: label ?? ELEMENT_ACTION_LABELS[kind], title, onClick });
  };

  push('focus', focus.title(subject), handlers.onFocus, focus.label);
  push('copyId', `Copy the id of ${subject}`, handlers.onCopyId);

  if (hasAsset) {
    push('createTicket', `Open a service call for ${subject}`, handlers.onCreateTicket);
    push('viewTickets', `Show the tickets that reference ${subject}`, handlers.onViewTickets);
    push('addIot', `Attach or create an IoT device on ${subject}`, handlers.onAddIot);
    push('showInDiagram', `Show ${subject} in the system diagram`, handlers.onShowInDiagram);
    push('editAsset', `Edit the asset for ${subject}`, handlers.onEditAsset);
    // Destructive, so it is deliberately the last thing in the list.
    push('deleteAsset', `Delete the asset for ${subject} (the model element is not affected)`,
      handlers.onDeleteAsset);
  } else {
    push('createAsset', `Create an asset from ${subject}`, handlers.onCreateAsset);
    push('createAssetAndTicket',
      `Create an asset from ${subject} and open a ticket on it`,
      handlers.onCreateAssetAndTicket);
    push('createAssetAndAddIot',
      `Create an asset from ${subject} and bind an IoT device to it`,
      handlers.onCreateAssetAndAddIot);
  }

  return actions;
}

/** Translation keys for the same action vocabulary; hosts still decide availability. */
export const ELEMENT_ACTION_LABEL_KEYS: Record<ElementActionKind, string> = {
  focus: 'dt_element_action_focus', copyId: 'dt_element_action_copyId',
  createAsset: 'dt_element_action_createAsset', createAssetAndAddIot: 'dt_element_action_createAssetAndAddIot',
  createAssetAndTicket: 'dt_element_action_createAssetAndTicket',
  createTicket: 'dt_element_action_createTicket', viewTickets: 'dt_element_action_viewTickets',
  addIot: 'dt_element_action_addIot', showInDiagram: 'dt_element_action_showInDiagram',
  editAsset: 'dt_element_action_editAsset', deleteAsset: 'dt_element_action_deleteAsset',
};
